import { useState, useEffect } from "react";
import { useAuth, api } from "@/App";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Gift, Car, Ticket, ArrowRight, Star, Crown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const { loyalty, user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get("/customer/dashboard");
        setDashboardData(response.data);
      } catch (error) {
        console.error("Failed to fetch dashboard", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const getTierGradient = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'gold': return 'from-[#C9A962] via-[#D4B978] to-[#A68B4B]';
      case 'platinum': return 'from-[#E5E4E2] via-[#F5F5F5] to-[#D0D0D0]';
      default: return 'from-[#A8A8A8] via-[#C0C0C0] to-[#909090]';
    }
  };

  const getTierIcon = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'gold': return <Crown className="w-6 h-6" />;
      case 'platinum': return <Sparkles className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <CustomerLayout title="Dashboard">
        <div className="space-y-6">
          <div className="h-56 rounded-2xl animate-shimmer" />
          <div className="grid grid-cols-2 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 rounded-xl animate-shimmer" />)}
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="space-y-8 animate-fade-in">
        {/* Welcome Hero Card */}
        <div className="relative overflow-hidden rounded-2xl luxury-card">
          {/* Background decorations */}
          <div className="absolute inset-0 hero-pattern opacity-50" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A962]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C9A962]/3 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          {/* Top gold line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A962]/50 to-transparent" />
          
          <div className="relative z-10 p-6 md:p-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              {/* Left side - Welcome & Balance */}
              <div className="flex-1">
                <p className="text-[#9A9A9A] text-sm tracking-widest uppercase mb-2">Welcome back</p>
                <h1 className="text-3xl md:text-4xl font-display font-medium text-white mb-6">
                  {user?.name}
                </h1>
                
                <div className="flex items-end gap-6">
                  <div>
                    <p className="text-[#5A5A5A] text-xs tracking-widest uppercase mb-2">Available Miles</p>
                    <p className="text-5xl md:text-6xl font-display font-medium text-gradient-luxury" data-testid="points-balance">
                      {loyalty?.current_points?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className={`tier-badge tier-${loyalty?.tier?.toLowerCase() || 'silver'}`}>
                    {getTierIcon(loyalty?.tier)}
                    <span>{loyalty?.tier || 'Silver'}</span>
                  </div>
                </div>
              </div>
              
              {/* Right side - Tier Progress */}
              <div className="lg:w-80">
                <div className="glass-gold rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[#9A9A9A] text-sm">Tier Progress</span>
                    {dashboardData?.tier_progress?.next_tier && (
                      <span className="text-[#C9A962] text-sm font-medium">
                        {dashboardData.tier_progress.progress_percent}%
                      </span>
                    )}
                  </div>
                  
                  <div className="progress-bar mb-4">
                    <div 
                      className="progress-fill"
                      style={{ width: `${dashboardData?.tier_progress?.progress_percent || 0}%` }}
                    />
                  </div>
                  
                  {dashboardData?.tier_progress?.next_tier ? (
                    <p className="text-[#5A5A5A] text-sm">
                      <span className="text-white font-medium">
                        {dashboardData.tier_progress.points_to_next.toLocaleString()}
                      </span> miles to <span className="text-[#C9A962]">{dashboardData.tier_progress.next_tier}</span>
                    </p>
                  ) : (
                    <p className="text-[#C9A962] text-sm">You have reached the highest tier</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/rewards" className="luxury-card luxury-card-hover p-6 group" data-testid="quick-rewards-card">
            <div className="icon-luxury mb-4 group-hover:scale-110 transition-transform">
              <Gift size={22} />
            </div>
            <p className="text-white font-medium mb-1">Rewards</p>
            <p className="text-[#5A5A5A] text-sm">Browse catalog</p>
          </Link>
          
          <Link to="/history" className="luxury-card luxury-card-hover p-6 group" data-testid="quick-rides-card">
            <div className="icon-luxury mb-4 group-hover:scale-110 transition-transform">
              <Car size={22} />
            </div>
            <p className="text-white font-medium mb-1">Ride History</p>
            <p className="text-[#5A5A5A] text-sm">{dashboardData?.recent_rides?.length || 0} trips</p>
          </Link>
          
          <Link to="/vouchers" className="luxury-card luxury-card-hover p-6 group" data-testid="quick-vouchers-card">
            <div className="icon-luxury mb-4 group-hover:scale-110 transition-transform">
              <Ticket size={22} />
            </div>
            <p className="text-white font-medium mb-1">Vouchers</p>
            <p className="text-[#5A5A5A] text-sm">
              {dashboardData?.recent_vouchers?.filter(v => v.status === 'active').length || 0} active
            </p>
          </Link>
          
          <div className="luxury-card p-6">
            <div className="w-12 h-12 rounded-lg bg-[#5CB85C]/10 border border-[#5CB85C]/20 flex items-center justify-center mb-4">
              <TrendingUp size={22} className="text-[#5CB85C]" />
            </div>
            <p className="text-white font-medium mb-1">Lifetime</p>
            <p className="text-[#5CB85C] text-lg font-display">{loyalty?.lifetime_points?.toLocaleString() || 0}</p>
          </div>
        </div>

        {/* Recent Activity */}
        <Card className="luxury-card border-0">
          <CardHeader className="flex flex-row items-center justify-between border-b border-[#252525] pb-6">
            <div>
              <CardTitle className="text-white font-display text-xl">Recent Activity</CardTitle>
              <p className="text-[#5A5A5A] text-sm mt-1">Your latest transactions</p>
            </div>
            <Link to="/history" className="text-[#C9A962] text-sm hover:text-[#D4B978] transition-colors flex items-center gap-2 group">
              View all 
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </CardHeader>
          <CardContent className="pt-6">
            {dashboardData?.recent_activity?.length > 0 ? (
              <div className="space-y-1">
                {dashboardData.recent_activity.slice(0, 5).map((event, idx) => (
                  <div key={idx} className="flex items-center gap-5 py-4 border-b border-[#252525]/50 last:border-0">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                      event.type === 'points_earned' ? 'bg-[#5CB85C]/10 text-[#5CB85C]' :
                      event.type === 'redemption' ? 'bg-[#C9A962]/10 text-[#C9A962]' :
                      event.type === 'tier_upgrade' ? 'bg-[#E5E4E2]/10 text-[#E5E4E2]' :
                      'bg-[#9A9A9A]/10 text-[#9A9A9A]'
                    }`}>
                      {event.type === 'points_earned' ? <TrendingUp size={18} /> :
                       event.type === 'redemption' ? <Gift size={18} /> :
                       event.type === 'tier_upgrade' ? <Crown size={18} /> :
                       <Car size={18} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{event.description}</p>
                      <p className="text-[#5A5A5A] text-xs mt-1">{formatDate(event.created_at)}</p>
                    </div>
                    {event.metadata?.points && (
                      <span className="text-[#5CB85C] font-display text-lg">+{event.metadata.points}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-4">
                  <Car size={28} className="text-[#5A5A5A]" />
                </div>
                <p className="text-[#9A9A9A] font-medium mb-1">No recent activity</p>
                <p className="text-[#5A5A5A] text-sm">Start earning miles with your first ride</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Rides */}
        {dashboardData?.recent_rides?.length > 0 && (
          <Card className="luxury-card border-0">
            <CardHeader className="flex flex-row items-center justify-between border-b border-[#252525] pb-6">
              <div>
                <CardTitle className="text-white font-display text-xl">Recent Rides</CardTitle>
                <p className="text-[#5A5A5A] text-sm mt-1">Miles earned from your journeys</p>
              </div>
              <Link to="/history" className="text-[#C9A962] text-sm hover:text-[#D4B978] transition-colors flex items-center gap-2 group">
                View all
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-1">
                {dashboardData.recent_rides.slice(0, 3).map((ride, idx) => (
                  <div key={idx} className="flex items-center justify-between py-4 border-b border-[#252525]/50 last:border-0">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-[#1A1A1A] border border-[#252525] flex items-center justify-center">
                        <Car size={18} className="text-[#9A9A9A]" />
                      </div>
                      <div>
                        <p className="text-white font-medium capitalize">{ride.vehicle_type}</p>
                        <p className="text-[#5A5A5A] text-sm">
                          {ride.distance_miles} mi • ${(ride.fare_cents / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[#5CB85C] font-display text-lg">+{ride.points_earned}</p>
                      <p className="text-[#5A5A5A] text-xs">{formatDate(ride.ride_date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </CustomerLayout>
  );
};

export default Dashboard;
